/**
 * tableSort.js — IDI-479: client-side click-to-sort for the shared Table.
 *
 * Pure comparators. A column is declared as
 *   { key, type: 'text' | 'date' | 'number' | 'money', value: row => raw }
 *
 * - text:   localeCompare, case-insensitive, numeric-aware ("Q-2" < "Q-10").
 * - date:   parsed to an epoch, so dates sort chronologically, never as strings.
 * - number: Number().
 * - money:  integer cents (Math.round(Number(v))). Callers pass the same cents
 *           value the cell formats, so $20 always sorts before $100.
 *
 * Null, undefined, '', '—' (and unparseable values) always sort LAST,
 * whatever the direction. The sort is stable: ties keep incoming order,
 * which is the page's default order.
 */

const EMPTY_MARKERS = new Set(['', '—', '–', '-']);

function isEmptyRaw(raw) {
  if (raw === null || raw === undefined) return true;
  if (typeof raw === 'string' && EMPTY_MARKERS.has(raw.trim())) return true;
  return false;
}

/**
 * Normalise a raw cell value to a comparable key, or null when it should
 * sort last.
 */
export function normalizeSortValue(raw, type = 'text') {
  if (isEmptyRaw(raw)) return null;
  switch (type) {
    case 'date': {
      if (raw instanceof Date) {
        const t = raw.getTime();
        return Number.isNaN(t) ? null : t;
      }
      if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
      const t = Date.parse(String(raw));
      return Number.isNaN(t) ? null : t;
    }
    case 'number': {
      const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[,\s]/g, ''));
      return Number.isFinite(n) ? n : null;
    }
    case 'money': {
      const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[$,\s]/g, ''));
      return Number.isFinite(n) ? Math.round(n) : null;
    }
    case 'text':
    default:
      return String(raw);
  }
}

const textCollator =
  typeof Intl !== 'undefined' && Intl.Collator
    ? new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })
    : null;

export function compareText(a, b) {
  if (textCollator) return textCollator.compare(a, b);
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
}

/** Compare two normalised (non-null) keys of the given type, ascending. */
export function compareValues(a, b, type = 'text') {
  if (type === 'text') return compareText(a, b);
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/**
 * Return a new array sorted by `column` in `direction` ('asc' | 'desc').
 * Without a column or direction, returns a shallow copy in input order.
 */
export function sortRows(rows, column, direction) {
  const list = Array.isArray(rows) ? rows : [];
  if (!column || (direction !== 'asc' && direction !== 'desc')) return list.slice();
  const type = column.type || 'text';
  const read = typeof column.value === 'function' ? column.value : (row) => row?.[column.key];
  const factor = direction === 'desc' ? -1 : 1;
  const decorated = list.map((row, index) => {
    let raw;
    try {
      raw = read(row);
    } catch (e) {
      raw = null;
    }
    return { row, index, key: normalizeSortValue(raw, type) };
  });
  decorated.sort((x, y) => {
    const xEmpty = x.key === null;
    const yEmpty = y.key === null;
    if (xEmpty && yEmpty) return x.index - y.index;
    if (xEmpty) return 1; // empties last regardless of direction
    if (yEmpty) return -1;
    const c = compareValues(x.key, y.key, type) * factor;
    return c !== 0 ? c : x.index - y.index; // stable
  });
  return decorated.map((d) => d.row);
}

/** asc → desc → off on the same column; a different column starts at asc. */
export function nextSortState(current, key) {
  const { sortKey, sortDir } = current || {};
  if (sortKey !== key) return { sortKey: key, sortDir: 'asc' };
  if (sortDir === 'asc') return { sortKey: key, sortDir: 'desc' };
  return { sortKey: null, sortDir: null };
}
